import React, { useCallback, useEffect } from 'react';

import {
  PageHeader,
  type PageHeaderAction,
} from '../../../../components/PageHeader';
import { TableHeader as CustomTableHeader } from '../../../../components/Table';
import {
  useGetOrdersLazyQuery,
  type GetOrdersQuery,
} from '../../../../generated';
import { formatOrdersResponse } from '../../data/utils/orderUtils';
import { useQueryVariables, useTableRowClick } from '../../hooks';
import { OrderDetailsPanel } from '../OrderDetails';

import {
  OrderListErrorState,
  OrderTable,
  OrdersTablePagination,
} from './components';

/**
 * Extracts total from a typed GetOrdersQuery response without using `any` casts.
 */
const getTotalFromResponse = (data: GetOrdersQuery | undefined): number => {
  const result = data?.searchOrder;
  if (result?.__typename === 'SearchOrderConnection') {
    return result.total;
  }
  return 0;
};

export const OrderList: React.FC = () => {
  const { variables, pagination, search } = useQueryVariables();

  const {
    pageSize,
    cursorAfter,
    hasNextPage,
    hasPreviousPage,
    setHasNextPage,
    setPageSize,
    goToNextPage,
    goToPreviousPage,
    resetCursorStack,
  } = pagination;

  const { keywordsInputValue, onKeywordsInputChange } = search;

  const [executeQuery, { data, loading, error }] = useGetOrdersLazyQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Fire query whenever variables change (search term, cursor, page size)
  useEffect(() => {
    executeQuery({ variables });
  }, [executeQuery, variables]);

  // Sync the next-page flag from the response; hasPreviousPage is derived
  // from the cursor stack inside usePagination.
  useEffect(() => {
    const result = data?.searchOrder;
    if (result?.__typename === 'SearchOrderConnection') {
      setHasNextPage(result.pageInfo.hasNextPage);
    }
  }, [data, setHasNextPage]);

  const { orders, pageInfo } = formatOrdersResponse(data);
  const totalOrders = getTotalFromResponse(data);

  const handleNextPage = useCallback(() => {
    if (pageInfo.endCursor) {
      goToNextPage(pageInfo.endCursor);
    }
  }, [pageInfo.endCursor, goToNextPage]);

  // Pop the cursor stack to return to the actual previous page. When the
  // stack is empty (e.g. the cursor was seeded from the URL) fall back to
  // resetting to page 1.
  const handlePreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      goToPreviousPage();
    } else {
      resetCursorStack();
    }
  }, [hasPreviousPage, goToPreviousPage, resetCursorStack]);

  // When the search term changes, clear the stack so results start from page 1.
  const handleSearchChange = useCallback(
    (value: string) => {
      resetCursorStack();
      onKeywordsInputChange(value);
    },
    [resetCursorStack, onKeywordsInputChange]
  );

  const { clickedOrderId, handleTableRowClick, resetClicked } =
    useTableRowClick();

  const headerActions: PageHeaderAction[] = [];

  if (error && orders.length === 0) {
    return (
      <div className="p-6 min-h-screen bg-background">
        <PageHeader
          title="Orders"
          description="Manage and view all orders"
          actions={headerActions}
        />
        <OrderListErrorState
          error={error}
          onRetry={() => executeQuery({ variables })}
        />
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-background">
      <PageHeader
        title="Orders"
        description="Manage and view all orders"
        actions={headerActions}
      />

      <div className="bg-content1 rounded-lg shadow-sm border border-default-200">
        <CustomTableHeader
          searchValue={keywordsInputValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search orders by number, buyer, or distributor..."
          isLoading={loading}
        />

        <OrderTable
          orders={orders}
          isLoading={loading}
          onOrderClick={handleTableRowClick}
        />

        {totalOrders > 0 && (
          <OrdersTablePagination
            hasMore={hasNextPage}
            hasPrevious={hasPreviousPage || cursorAfter !== undefined}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            isLoading={loading}
            currentPageCount={orders.length}
          />
        )}
      </div>

      {/* Order Details Side Panel */}
      <OrderDetailsPanel
        orderId={clickedOrderId || ''}
        isOpen={!!clickedOrderId}
        onClose={resetClicked}
      />
    </div>
  );
};
